import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { SalesOrderLineModal } from './SalesOrderLineModal';
import { SalesOrderStatusBadge } from './SalesOrderStatusBadge';
import {
  RELEASABLE_STATUSES,
  deleteSalesOrder,
  deleteSalesOrderLine,
  getSalesOrder,
  isFinalStatus,
  listSalesOrderLines,
  setSalesOrderStatus,
  type SalesOrderAction,
  type SalesOrderLineDto,
} from './api';

/**
 * One sales order: the header, its lines, and the three things a person can decide about it.
 *
 * A URL of its own rather than a dialog, for the reason the purchase order has one — an order is a
 * document worked on over minutes and handed between people, so it has to be something you can
 * return to or send to a colleague.
 *
 * WHICH ACTIONS APPEAR, AND WHY
 * ------------------------------
 * Release shows only from `open` or `back_order`, which is legacy's own rule: the remainder of a
 * short-shipped order is released again to be picked again. Close shows on anything not already
 * closed, and needs orders:cancel — because closing IS how an order is cancelled, and nothing
 * else takes one out of play.
 *
 * REOPEN shows on any closed order, and takes orders:cancel too (tenant script 071). It used to
 * hide itself when anything had shipped, which turned out to be the only case anybody wanted: an
 * order picked short, shipped, and then closed by hand had no way back. What it reopens TO is the
 * server's decision — nothing shipped gives `open`, some gives `back_order`, all gives `shipped` —
 * so the screen does not promise a result it cannot know.
 *
 * The server enforces every one of these; the screen only avoids offering what it knows will be
 * refused. Hiding a button is a courtesy, not a control.
 */
export function SalesOrderDetailRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { id } = useParams({ strict: false }) as { id: string };

  const [lineModalOpen, { open: openLineModal, close: closeLineModal }] = useDisclosure(false);
  const [editingLine, setEditingLine] = useState<SalesOrderLineDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canWrite = permissions.has('orders:write');
  const canCancel = permissions.has('orders:cancel');

  const order = useQuery({
    queryKey: ['sales-order', id] as const,
    queryFn: ({ signal }) => getSalesOrder(id, signal),
  });

  const lines = useQuery({
    queryKey: ['sales-order-lines', id] as const,
    queryFn: ({ signal }) => listSalesOrderLines(id, signal),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['sales-order', id] });
    void queryClient.invalidateQueries({ queryKey: ['sales-order-lines', id] });
    // The list's totals and status came from the same rows.
    void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
  }

  const act = useMutation({
    mutationFn: (action: SalesOrderAction) => setSalesOrderStatus(id, action),
    onSuccess: (updated) => {
      notifications.show({
        message: t(`salesOrders.actionDone.${updated.status}`, {
          defaultValue: t('salesOrders.saved'),
        }),
        color: 'green',
      });
      refresh();
    },
    onError: (err) => setActionError(describe(err)),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => deleteSalesOrderLine(id, lineId),
    onSuccess: () => {
      notifications.show({ message: t('salesOrders.lineDeleted'), color: 'green' });
      refresh();
    },
    onError: (err) => setActionError(describe(err)),
  });

  const removeOrder = useMutation({
    mutationFn: () => deleteSalesOrder(id),
    onSuccess: () => {
      notifications.show({ message: t('salesOrders.deleted'), color: 'green' });
      void queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      void navigate({ to: '/app/sales-orders' });
    },
    onError: (err) => setActionError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      // The server's own message names the order and says what to do instead ("close it — what
      // went out stays on the books"), which is more use than anything composed here.
      return err.problem.detail ?? err.problem.title ?? t('salesOrders.errors.actionFailed');
    }
    return t('salesOrders.errors.actionFailed');
  }

  const lineColumns: DataColumn<SalesOrderLineDto>[] = useMemo(
    () => [
      {
        key: 'line',
        label: t('salesOrders.line'),
        card: 'subtitle',
        render: (l) => <Text size="sm">{l.lineNumber}</Text>,
      },
      {
        key: 'item',
        label: t('salesOrders.item'),
        card: 'title',
        render: (l) => (
          <Stack gap={0}>
            <Text size="sm" fw={500}>
              {l.itemNumber}
            </Text>
            <Text size="xs" c="dimmed">
              {l.itemDescription}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'ordered',
        label: t('salesOrders.quantity'),
        render: (l) => (
          <Text size="sm">
            {l.quantityOrdered} {l.uomCode}
          </Text>
        ),
      },
      {
        key: 'shipped',
        label: t('salesOrders.shipped'),
        render: (l) => <Text size="sm">{l.quantityShipped}</Text>,
      },
      {
        key: 'outstanding',
        // legacy's BalanceDue. The number the warehouse actually works from, and the reason it is
        // computed by the server rather than subtracted in three different screens.
        label: t('salesOrders.outstanding'),
        render: (l) =>
          l.quantityOutstanding > 0 ? (
            <Text size="sm" fw={500}>
              {l.quantityOutstanding}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          ),
      },
      {
        key: 'price',
        label: t('salesOrders.unitPrice'),
        // The price AND why it is that price. A discounted line that does not say what discounted
        // it is a phone call, and the answer is not re-derivable later once the tiers have moved
        // on -- which is why 075 stores it on the row rather than recalculating.
        render: (l) => (
          <Stack gap={0}>
            <Text size="sm">{money(l.unitPrice, order.data?.currencyCode)}</Text>
            {l.priceSource && l.priceSource !== 'list' && (
              <Text size="xs" c="dimmed">
                {t(`pricing.source.${l.priceSource}`, { defaultValue: l.priceSource })}
                {l.discount > 0 && ` · ${Math.round(l.discount * 10000) / 100}%`}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'total',
        label: t('salesOrders.lineTotal'),
        card: 'aside',
        render: (l) => <Text size="sm">{money(l.lineTotal, order.data?.currencyCode)}</Text>,
      },
    ],
    [t, order.data?.currencyCode],
  );

  if (order.isLoading) {
    return (
      <Center py="xl">
        <Loader color="invetrak" />
      </Center>
    );
  }

  if (order.isError) {
    const notFound = order.error instanceof ApiError && order.error.status === 404;
    return (
      <Stack gap="md">
        <Anchor onClick={() => void navigate({ to: '/app/sales-orders' })}>
          <Group gap={4}>
            <IconArrowLeft size={16} />
            {t('salesOrders.backToList')}
          </Group>
        </Anchor>
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {/* 404 here means the order is in a different warehouse as often as it means no such
              order — the guard answers the same either way, deliberately, so the message says
              both rather than claiming something it cannot know. */}
          {notFound ? t('salesOrders.errors.notFoundHere') : (order.error as Error).message}
        </Alert>
      </Stack>
    );
  }

  const so = order.data!;
  const rows = lines.data ?? [];
  const final = isFinalStatus(so.status);
  const editable = canWrite && !final;
  const canRelease = editable && RELEASABLE_STATUSES.includes(so.status) && so.lineCount > 0;
  // Not `&& !so.hasShipments` any more: a part shipped order that somebody closed is exactly
  // what needs reopening, and 071 lets it. orders:cancel because it is the inverse of closing.
  const canReopen = canCancel && so.status === 'closed';
  const canClose = canWrite && canCancel && !final;
  const canDeleteOrder = canWrite && !so.hasShipments && !so.hasAllocations;

  return (
    <>
      <PageHeader
        title={so.orderNumber}
        back
        subtitle={
          <Group gap="sm" wrap="wrap">
            <SalesOrderStatusBadge status={so.status} />
            {!so.acceptPartial && (
              // Worth saying up here: it changes what a short pick is allowed to do, and the person
              // deciding whether to ship what they have needs to know before they decide.
              <Badge size="sm" variant="light" color="grape">
                {t('salesOrders.completeOnly')}
              </Badge>
            )}
            <Text size="sm" c="dimmed">
              {so.customerNumber} — {so.customerName}
            </Text>
          </Group>
        }
        actions={
          <Group gap="xs" wrap="wrap">
            {canRelease && (
              <Button
                color="invetrak"
                loading={act.isPending}
                onClick={() => act.mutate('release')}
              >
                {so.status === 'back_order'
                  ? t('salesOrders.releaseRemainder')
                  : t('salesOrders.release')}
              </Button>
            )}
            {canReopen && (
              <Button variant="default" loading={act.isPending} onClick={() => act.mutate('reopen')}>
                {t('salesOrders.reopen')}
              </Button>
            )}
            {canClose && (
              <Button
                variant="light"
                color="red"
                loading={act.isPending}
                onClick={() => act.mutate('close')}
              >
                {t('salesOrders.close')}
              </Button>
            )}
            {canDeleteOrder && (
              <Button
                variant="subtle"
                color="red"
                loading={removeOrder.isPending}
                onClick={() => removeOrder.mutate()}
              >
                {t('salesOrders.delete')}
              </Button>
            )}
          </Group>
        }
      />

      <Stack gap="md">
        {actionError && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            withCloseButton
            onClose={() => setActionError(null)}
          >
            {actionError}
          </Alert>
        )}

        {so.status === 'back_order' && (
          // The status people misread. An order shipped short reads as "shipped" at a glance, and
          // then the rest of it is nobody's job — which is why legacy counts it as OPEN and runs a
          // nightly job chasing them.
          <Alert color="orange" variant="light">
            {t('salesOrders.backOrderExplained')}
          </Alert>
        )}

        <Card withBorder padding="md" radius="sm">
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <Field label={t('salesOrders.customer')} value={`${so.customerNumber} — ${so.customerName}`} />
            <Field
              label={t('salesOrders.shipTo')}
              value={so.shipToName ? `${so.shipToCode} — ${so.shipToName}` : t('salesOrders.noShipToSet')}
            />
            <Field label={t('salesOrders.customerPo')} value={so.customerPo} />
            <Field label={t('salesOrders.orderedBy')} value={so.orderedBy} />
            <Field label={t('salesOrders.ordered')} value={new Date(so.orderDateUtc).toLocaleDateString()} />
            <Field
              label={t('salesOrders.requested')}
              value={so.requestedShipDateUtc ? new Date(so.requestedShipDateUtc).toLocaleDateString() : null}
            />
            <Field label={t('salesOrders.warehouse')} value={so.warehouseCode} />
            <Field label={t('salesOrders.terms')} value={so.paymentTermCode} />
          </SimpleGrid>

          {so.specialInstructions && (
            <>
              <Divider my="sm" />
              <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                {t('salesOrders.instructions')}
              </Text>
              <Text size="sm">{so.specialInstructions}</Text>
            </>
          )}
        </Card>

        <Group justify="space-between" align="center" wrap="wrap">
          <Title order={4}>{t('salesOrders.linesTitle')}</Title>
          {editable && (
            <Button
              size="xs"
              variant="light"
              color="invetrak"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setEditingLine(null);
                openLineModal();
              }}
            >
              {t('salesOrders.addLine')}
            </Button>
          )}
        </Group>

        {lines.isLoading && (
          <Center py="md">
            <Loader size="sm" color="invetrak" />
          </Center>
        )}

        {lines.isSuccess && rows.length === 0 && (
          <Alert color="yellow" variant="light">
            {/* Not a neutral empty state: an order with no lines cannot be released, and this is
                the screen where that gets fixed. */}
            {t('salesOrders.noLines')}
          </Alert>
        )}

        {rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={lineColumns}
            rowKey={(l) => l.salesOrderLineId}
            minWidth={820}
            actionsWidth={80}
            striped
            actions={
              editable
                ? (l) => (
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        color="invetrak"
                        aria-label={t('salesOrders.editLine', { number: l.lineNumber })}
                        onClick={() => {
                          setEditingLine(l);
                          openLineModal();
                        }}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t('salesOrders.deleteLine', { number: l.lineNumber })}
                        loading={removeLine.isPending}
                        onClick={() => removeLine.mutate(l.salesOrderLineId)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  )
                : undefined
            }
          />
        )}

        <Card withBorder padding="md" radius="sm">
          <Stack gap={4} align="flex-end">
            <Totals label={t('salesOrders.subtotal')} value={money(so.subtotalAmount, so.currencyCode)} />
            <Totals label={t('salesOrders.freight')} value={money(so.freightAmount, so.currencyCode)} />
            <Totals label={t('salesOrders.tax')} value={money(so.taxAmount, so.currencyCode)} />
            <Divider w={220} my={4} />
            <Totals label={t('salesOrders.total')} value={money(so.totalAmount, so.currencyCode)} strong />
          </Stack>
        </Card>
      </Stack>

      <SalesOrderLineModal
        opened={lineModalOpen}
        onClose={closeLineModal}
        salesOrderId={id}
        line={editingLine}
        onSaved={() => {
          notifications.show({ message: t('salesOrders.lineSaved'), color: 'green' });
          refresh();
        }}
      />
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </Stack>
  );
}

function Totals({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Group gap="xl" w={220} justify="space-between">
      <Text size="sm" c={strong ? undefined : 'dimmed'} fw={strong ? 600 : undefined}>
        {label}
      </Text>
      <Text size="sm" fw={strong ? 600 : undefined}>
        {value}
      </Text>
    </Group>
  );
}

function money(amount: number, currency: string | undefined): string {
  return amount.toLocaleString(undefined, { style: 'currency', currency: currency ?? 'USD' });
}
